import { useState } from 'react';
import { api } from '../lib/api.js';

// ─── DocumentUploadModal ──────────────────────────────────────────────────────
export function DocumentUploadModal({ reminderId, location, user, onClose, onSave }) {
  const [form, setForm] = React.useState({ name: '', category: 'other', expiration_date: '', notes: '' });
  const [fileData, setFileData] = React.useState(null);
  const [fileType, setFileType] = React.useState(null);
  const [fileSize, setFileSize] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!form.name) set('name', file.name.replace(/\.[^.]+$/, ''));
    setFileType(file.type);
    setFileSize(file.size);
    const reader = new FileReader();
    reader.onload = ev => setFileData(ev.target.result);
    reader.readAsDataURL(file);
  }

  async function handleSave() {
    if (!form.name.trim() || !fileData) return;
    setSaving(true);
    try {
      await api('POST', '/api/compliance/documents', {
        reminder_id: reminderId || null, name: form.name.trim(), category: form.category,
        file_data: fileData, file_type: fileType, file_size: fileSize,
        expiration_date: form.expiration_date || null, location_id: location?.id || null,
        uploaded_by: user.id, notes: form.notes || null,
      });
      onSave();
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  }

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-ink focus:outline-none focus:ring-2 focus:ring-crimson/30';
  const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-cream w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col fade-in shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-gold rounded-t-2xl p-5 flex items-center justify-between flex-shrink-0">
          <h3 className="text-ink font-bold text-lg">Upload Document</h3>
          <button onClick={onClose} className="text-ink/60 hover:text-ink text-2xl leading-none">×</button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <div>
            <label className={labelCls}>File</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFile}
              className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-crimson file:text-white hover:file:bg-crimson-dark" />
            {fileData && <p className="text-xs text-green-600 mt-1">✓ File loaded ({fileSize ? `${(fileSize/1024).toFixed(0)} KB` : ''})</p>}
          </div>
          <div>
            <label className={labelCls}>Document Name</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} className={inputCls} placeholder="e.g. Liquor License 2026" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} className={inputCls}>
                {['License','Permit','Insurance','Tax Filing','Lease','Contract','Other'].map(c => (
                  <option key={c} value={c.toLowerCase().replace(' ', '_')}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Expiration Date</label>
              <input type="date" value={form.expiration_date} onChange={e => set('expiration_date', e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} className={inputCls} rows={2} />
          </div>
        </div>
        <div className="p-5 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim() || !fileData}
            className="flex-1 py-2.5 bg-gold text-ink rounded-xl text-sm font-semibold hover:bg-gold-light disabled:opacity-50">
            {saving ? 'Uploading...' : 'Upload Document'}
          </button>
        </div>
      </div>
    </div>
  );
}
